import { useRef, useEffect, useState, useCallback, useMemo, useContext } from "react";
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import 'mapbox-gl/dist/mapbox-gl.css';
import './map.css';
import PropTypes from "prop-types";
import FilterMenu, { policyTypeOptions, policySubtypeOptions } from './FilterMenu/FilterMenu';
import ZoomTo from './ZoomTo/ZoomTo';
import { ModalContext } from '../context/ModalContext.js';
// importing geojson sources & Componenets
import MCLinks from '../geojson/community_profiles_links.json'
import MetroCounties from '../geojson/metro_counties.json'
import MetroCTU from '../geojson/metro_ctus.json'
import MetroLines from '../geojson/metro_ctus_lines.json'
import MetroCentroids from '../geojson/metro_county_centroids.json'
import Policies from '../geojson/policies_all_ctus_counties.json'
import CD_Labels from '../geojson/msp_cd_centroids.geojson'
import CulturalDistricts from '../geojson/msp_cultural_dists.geojson'
import MenuButtons from './Navbar/MenuButtons/MenuButtons'
import Legend from './Legend/Legend'
mapboxgl.accessToken = 'pk.eyJ1Ijoic3JmZGV2IiwiYSI6ImNtcG1yZjZmZDIwazQycXBzaHZmOHB5M2QifQ.iqNBssMSQ9h5H670C1otBA';

// Helper function to normalize township names
const normalizeEntityName = (name) => {
  // Convert "Twp." to "Township"
  return name.replace(/\bTwp\./g, 'Township');
};



const Map = ({ onFilterChange, isFilterMenuVisible, setIsFilterMenuVisible, clearFiltersTrigger }) => {
    const mapContainer = useRef(null);
    const [mapInstance, setMapInstance] = useState(null);
    const [layersFilters] = useState([]);
    const [activeLayer, setActiveLayer] = useState(null);
    const { openModal } = useContext(ModalContext);
    const mapClickPopupRef = useRef(null); // Add this ref to track map-click popups
    const zoomToRef = useRef(null); // Add this ref to access ZoomTo methods
    const [filters, setFilters] = useState({
        policyTypes: [],
        policySubtypes: [],
        communitySize: [],
        geographyType: [],
        populationRange: null,
        cityPopulation: null,
        countyPopulation: null
    });
    const [isZoomToOpen, setIsZoomToOpen] = useState(false);

    const populationLegend = [
      {color: '#9bbbf2', label: 'Less than 1,000'},
      {color: '#8395b3', label: '1,000-5,000'},
      {color: '#606c82', label: '5,000-15,000'},
      {color: '#364766', label: '15,000-30,000'},
      {color: '#18273f', label: '30,000-50,000'},
      {color: '#061123', label: '50,000-429,954'}
    ];

    const policyLegend = [
      {color: '#fafdf4', label: '0'},
      {color: '#e5f2d2', label: '1-5'},
      {color: '#9cc066', label: '6-12'},
      {color: '#657945', label: '13-25'},
      {color: '#2d381d', label: '26-31'}
    ];

    const countyLegend = [
      {color: '#feefa4', label: '0'},
      {color: '#ecc781', label: '5'},
      {color: '#dd8346', label: '7'},
      {color: '#e3803f', label: '9'}
    ];
      

    const handleFilterChange = useCallback((newFilters) => {
        setFilters(newFilters);
        onFilterChange(newFilters);
    }, []);

    const toggleFilterMenu = useCallback(() => {
        setIsFilterMenuVisible(prev => !prev);
    }, [setIsFilterMenuVisible]);

    const toggleZoomTo = useCallback(() => {
        setIsZoomToOpen(prev => {
            const newValue = !prev;
            // If opening zoom to menu, close filter menu
            if (newValue) {
                setIsFilterMenuVisible(false);
            }
            return newValue;
        });
    }, []);

    // Close functions for external menu management
    const closeFilterMenu = useCallback(() => {
        setIsFilterMenuVisible(false);
    }, []);

    const closeZoomToMenu = useCallback(() => {
        setIsZoomToOpen(false);
    }, []);

    // Function to apply filters to the map
    const applyFilters = useCallback(() => {
        if (!mapInstance || !mapInstance.isStyleLoaded()) {
            console.log('Map not ready for filters, will try again later');
            return;
        }

        console.log('Current filters state:', filters);

        // Check if any filters are active
        const hasAnyFilters = filters.policyTypes.length > 0 || 
            filters.policySubtypes.length > 0 ||
            (filters.populationRange && filters.populationRange.minThreshold !== undefined && filters.populationRange.maxThreshold !== undefined) ||
            (filters.cityPopulation && filters.cityPopulation.minThreshold !== undefined && filters.cityPopulation.maxThreshold !== undefined) ||
            (filters.countyPopulation && filters.countyPopulation.minThreshold !== undefined && filters.countyPopulation.maxThreshold !== undefined) ||
            filters.communitySize.length > 0 ||
            filters.geographyType.length > 0;

        // Check if only population-based filters are active (no policy filters)
        const hasOnlyPopulationFilters = (
            (filters.populationRange && filters.populationRange.minThreshold !== undefined && filters.populationRange.maxThreshold !== undefined) ||
            (filters.cityPopulation && filters.cityPopulation.minThreshold !== undefined && filters.cityPopulation.maxThreshold !== undefined) ||
            (filters.countyPopulation && filters.countyPopulation.minThreshold !== undefined && filters.countyPopulation.maxThreshold !== undefined) ||
            filters.communitySize.length > 0 ||
            filters.geographyType.length > 0
        ) && filters.policyTypes.length === 0 && filters.policySubtypes.length === 0;

        // Check if policy filters are active
        const hasPolicyFilters = filters.policyTypes.length > 0 || filters.policySubtypes.length > 0;
        const hasPopulationFilters = filters.populationRange || filters.cityPopulation || filters.countyPopulation;
        const hasOnlyGeographyFilter = !hasPolicyFilters && !hasPopulationFilters && filters.geographyType.length > 0;

        if (hasAnyFilters) {
            console.log('Making policies layer visible');
            mapInstance.setLayoutProperty('policies', 'visibility', 'visible');
            
            // Set paint properties based on filter types
            if (filters.policyTypes.length > 0) {
                const colorExpression = [
                    'match',
                    ['get', 'category'],
                    ...filters.policyTypes.flatMap(type => [
                        type,
                        policyTypeOptions.find(option => option.value === type)?.color || '#000000'
                    ]),
                    '#000000' // default color
                ];
                
                mapInstance.setPaintProperty('policies', 'fill-color', colorExpression);
                mapInstance.setPaintProperty('policies', 'fill-opacity', 0.7);
            } else if (filters.policySubtypes.length > 0) {
                // Find parent policy types for selected subtypes
                const parentTypes = new Set();
                Object.entries(policySubtypeOptions).forEach(([parentType, subtypes]) => {
                    if (subtypes.some(subtype => filters.policySubtypes.includes(subtype.value))) {
                        parentTypes.add(parentType);
                    }
                });

                const colorExpression = [
                    'match',
                    ['get', 'category'],
                    ...Array.from(parentTypes).flatMap(type => [
                        type,
                        policyTypeOptions.find(option => option.value === type)?.color || '#000000'
                    ]),
                    '#000000' // default color
                ];
                
                mapInstance.setPaintProperty('policies', 'fill-color', colorExpression);
                mapInstance.setPaintProperty('policies', 'fill-opacity', 0.7);
            } else if (hasOnlyPopulationFilters && (filters.populationRange || filters.cityPopulation || filters.countyPopulation)) {
                // When only population filters are active, color by population ranges
                mapInstance.setPaintProperty('policies', 'fill-color', [
                    'interpolate',
                    ['linear'],
                    ['get', 'pop20'],
                    0, '#FFF59D',      // Less than 1,000
                    1000, '#FFEB3B',   // 1,000-5,000
                    5000, '#FDD835',   // 5,000-15,000
                    15000, '#F9A825',  // 15,000-30,000
                    30000, '#FF8F00',  // 30,000-50,000
                    50000, '#FF6F00',  // 50,000-100,000
                    100000, '#E65100', // 100,000-500,000
                    500000, '#BF360C'  // 500,000+
                ]);
                mapInstance.setPaintProperty('policies', 'fill-opacity', 0.7);
            } else if (hasOnlyGeographyFilter) {
                // When only geography type filters are active, use a light gray color
                mapInstance.setPaintProperty('policies', 'fill-color', [
                    'match',
                    ['get', 'geog_type'],
                    'CTU', '#e0e0e0',    // Light gray for cities
                    'County', '#f5f5f5', // Very light gray for counties
                    '#ffffff'            // White as default
                ]);
                mapInstance.setPaintProperty('policies', 'fill-opacity', 0.3);
            }
        } else {
            console.log('No active filters, hiding policies layer');
            mapInstance.setLayoutProperty('policies', 'visibility', 'none');
            return;
        }

        const allFilters = ["all"];

        // Policy Type filters - only apply when policy filters are active
        if (hasPolicyFilters && filters.policyTypes.length > 0) {
            const policyTypeFilter = ["any"];
            filters.policyTypes.forEach(type => {
                console.log('Adding policy type filter for:', type);
                policyTypeFilter.push(["==", "category", type]);
            });
            console.log('Policy type filter expression:', JSON.stringify(policyTypeFilter));
            allFilters.push(policyTypeFilter);
        }

        // Policy Subtype filters - only apply when policy filters are active
        if (hasPolicyFilters && filters.policySubtypes.length > 0) {
            const subtypeFilter = ["any"];
            filters.policySubtypes.forEach(subtype => {
                subtypeFilter.push(["==", "subcat", subtype]);
            });
            allFilters.push(subtypeFilter);
        }

        // Population Range filters (from slider)
        if (filters.populationRange && filters.populationRange.minThreshold !== undefined && filters.populationRange.maxThreshold !== undefined) {
            const populationFilter = [
                "all",
                [">=", "pop20", filters.populationRange.minThreshold],
                ["<=", "pop20", filters.populationRange.maxThreshold === 1400000 ? 9999999 : filters.populationRange.maxThreshold]
            ];
            allFilters.push(populationFilter);
            console.log('Adding population range filter:', JSON.stringify(populationFilter));
        }

        // Combined City and County Population filters
        const hasCityPopulation = filters.cityPopulation && filters.cityPopulation.minThreshold !== undefined && filters.cityPopulation.maxThreshold !== undefined;
        const hasCountyPopulation = filters.countyPopulation && filters.countyPopulation.minThreshold !== undefined && filters.countyPopulation.maxThreshold !== undefined;
        
        if (hasCityPopulation || hasCountyPopulation) {
            const populationFilters = ["any"]; // Use "any" to allow either city OR county to match
            
            if (hasCityPopulation) {
                const cityPopulationFilter = [
                    "all",
                    ["==", "geog_type", "CTU"], // Only apply to cities/townships
                    [">=", "pop20", filters.cityPopulation.minThreshold],
                    ["<=", "pop20", filters.cityPopulation.maxThreshold === 430000 ? 9999999 : filters.cityPopulation.maxThreshold]
                ];
                populationFilters.push(cityPopulationFilter);
                console.log('Adding city population filter:', JSON.stringify(cityPopulationFilter));
            }
            
            if (hasCountyPopulation) {
                const countyPopulationFilter = [
                    "all",
                    ["==", "geog_type", "County"], // Only apply to counties
                    [">=", "pop20", filters.countyPopulation.minThreshold],
                    ["<=", "pop20", filters.countyPopulation.maxThreshold === 1400000 ? 9999999 : filters.countyPopulation.maxThreshold]
                ];
                populationFilters.push(countyPopulationFilter);
                console.log('Adding county population filter:', JSON.stringify(countyPopulationFilter));
            }
            
            // Only add the population filter if we have at least one condition
            if (populationFilters.length > 1) {
                allFilters.push(populationFilters);
                console.log('Adding combined population filter:', JSON.stringify(populationFilters));
            }
        }

        // Community Size filters (legacy support)
        if (filters.communitySize.length > 0) {
            const sizeFilter = ["any"];
            filters.communitySize.forEach(size => {
                switch(size) {
                    case 'less_1000':
                        sizeFilter.push(["<=", "pop20", 1000]);
                        break;
                    case '1000_5000':
                        sizeFilter.push(
                            ["all", 
                                [">", "pop20", 1000],
                                ["<=", "pop20", 5000]
                            ]
                        );
                        break;
                    case '5000_15000':
                        sizeFilter.push(
                            ["all", 
                                [">", "pop20", 5000],
                                ["<=", "pop20", 15000]
                            ]
                        );
                        break;
                    case '15000_30000':
                        sizeFilter.push(
                            ["all", 
                                [">", "pop20", 15000],
                                ["<=", "pop20", 30000]
                            ]
                        );
                        break;
                    case '30000_50000':
                        sizeFilter.push(
                            ["all", 
                                [">", "pop20", 30000],
                                ["<=", "pop20", 50000]
                            ]
                        );
                        break;
                    case '50000_100000':
                        sizeFilter.push(
                            ["all", 
                                [">", "pop20", 50000],
                                ["<=", "pop20", 100000]
                            ]
                        );
                        break;
                    case '50000_plus':
                    case '100000_plus':
                        sizeFilter.push([">", "pop20", 100000]);
                        break;
                }
            });
            allFilters.push(sizeFilter);
        }

        // Geography Type filters
        if (filters.geographyType.length > 0) {
            const geographyFilter = ["any"];
            filters.geographyType.forEach(type => {
                // Map the filter values to match the data
                const mappedType = type === 'city' ? 'CTU' : 'County';
                geographyFilter.push(["==", "geog_type", mappedType]);
            });
            allFilters.push(geographyFilter);
        }

        // Apply filters to relevant layers
        try {
            console.log('Applying final filter expression:', JSON.stringify(allFilters));
            mapInstance.setFilter('policies', allFilters);

            // Debug: Check features after applying filter
            const features = mapInstance.querySourceFeatures('policies');
            console.log(`Total features in source: ${features.length}`);
            
            if (features.length === 0) {
                // Try to check the raw data from the source
                const source = mapInstance.getSource('policies');
                if (source && source._data) {
                    console.log('Raw source data features:', source._data.features.length);
                }
            }

            // Move the policies layer to the top of the layers
            mapInstance.moveLayer('policies');

        } catch (error) {
            console.error('Error applying filters:', error);
        }
    }, [mapInstance, filters]);

    // Apply filters when they change
    useEffect(() => {
        applyFilters();
    }, [filters, layersFilters]);

    // Remove any existing dynamic population layers when component mounts/unmounts
    useEffect(() => {
        // Clean up any existing dynamic layers since we're using filter-based approach now
        if (mapInstance && mapInstance.isStyleLoaded && mapInstance.isStyleLoaded()) {
            const existingLayers = ['population-dynamic-1', 'population-dynamic-2', 'population-dynamic-3'];
            existingLayers.forEach(layerId => {
                if (mapInstance.getLayer && mapInstance.getLayer(layerId)) {
                    try {
                        mapInstance.removeLayer(layerId);
                    } catch (error) {
                        console.warn(`Could not remove layer ${layerId}:`, error);
                    }
                }
            });
        }
    }, [mapInstance]);

    useEffect(() => {
      console.log("Map effect running, mapContainer exists:", !!mapContainer.current);
      
      if (mapContainer.current) {
        console.log("Initializing map...");
        const map = new mapboxgl.Map({
          container: mapContainer.current,
          style: 'mapbox://styles/mapbox/light-v9',
          zoom: 8.15,
          minZoom: 8.15,
          maxBounds: [
            [-97.0, 44.0],  
            [-90.0, 46.0]   
          ],
          center: [-93.247242, 44.944148],
          attributionControl: false
        });

        // Add error handling for map loading failures
        map.on('error', function(e) {
          console.error('Mapbox error:', e);
        });

        // Add more detailed logging for map load
        map.on('styledata', function() {
          console.log("Map style data loaded");
        });
        
        // Wait for map to fully load before performing operations or setting map instance
        map.on('load', function () {
          console.log("Map fully loaded");
          
          var layers = map.getStyle().layers;
          var firstSymbolId;
          for (var i = 0; i < layers.length; i++) {
            if (layers[i].type === 'symbol') {
              firstSymbolId = layers[i].id;
              break;
            }
          }
          
          // Only check layers when mapInstance is available
          if (mapInstance) {
            const layers = ["Population", "Policy Count by City", "Count by County-Adopted Policies", "Cultural Districts", "policies", "Satellite"];
            for (const layer of layers) {
              try {
                const visiblity = mapInstance.getLayoutProperty(layer,'visibility');
                if (visiblity === 'visible') {
                  setActiveLayer(layer);
                  break;
                } else {
                  setActiveLayer(null);
                }
              } catch (error) {
                console.warn(`Could not check layer ${layer}:`, error);
              }
            }
          } 
          
          
       

          
          map.addSource("mapbox-satellite", 
            { "type": "raster", 
              "url": "mapbox://mapbox.satellite", 
              "tileSize": 256 });
  
          map.addLayer({
            "type": "raster",
            "id": 'Satellite',
            "source": "mapbox-satellite",
            'layout': { 'visibility': 'none' }
          }, firstSymbolId);
  
          map.addSource('ctus', {
            'type': 'geojson',
            'data': MetroCTU
          });
  
          map.addSource('cd_labels', {
            'type': 'geojson',
            'data': CD_Labels
          });
  
          map.addSource('policies', {
            'type': 'geojson',
            'data': Policies
          });
  
                    map.addSource('centroids', {
            'type': 'geojson',
            'data': MetroCentroids
          });

          map.addSource('cult_dists', {
            'type': 'geojson',
            'data': CulturalDistricts
          });

          map.addSource('lines', {
            'type': 'geojson',
            'data': MetroLines
        });
          
          map.addLayer({
            'id': 'ctus-main',
            'type': 'fill',
            'source': 'ctus',
            'layout': { 'visibility': 'visible' },
            'paint': { 'fill-color': '#33C8BC', "fill-opacity": 0.1 }
          }, firstSymbolId);

          map.addLayer({
            'id': 'policies',
            'type': 'fill',
            'source': 'policies',
            'layout': { 'visibility': 'none' },
            'paint': { 'fill-color': ['get', 'color'], "fill-opacity": ['get', 'opacity'], 'fill-outline-color': '#6d6d6d' }
          }, firstSymbolId);

          // Move centroids layer to be the last layer added
          map.addLayer({
            'id': 'centroids',
            'type': 'symbol',
            'source': 'centroids',
            'layout': { 
              'visibility': 'visible',
              'text-field': ['get', 'CO_NAME'],
              'text-font': ['Open Sans Bold'],
              'text-size': 14,
              'text-transform': 'uppercase',
              'text-letter-spacing': 0.1,
              'text-offset': [0, 0],
              'text-anchor': 'center',
              'text-allow-overlap': true,
              'text-ignore-placement': true,
              'symbol-placement': 'point'
            },
            'paint': { 
              'text-color': '#e3803f',
              'text-halo-color': '#ffffff',
              'text-halo-width': 1.5,
              'text-opacity': 1
            }
          });
  
          map.addLayer({
            'id': 'ctu_boundaries',
            'type': 'line',
            'source': 'ctus',
            'layout': { 'visibility': 'visible' },
            'paint': { 'line-color': '#6d6d6d', 'line-width': 0.2 }
          }, firstSymbolId);
  
          map.addLayer({
            'id': 'ctus-darker',
            'type': 'fill',
            'source': 'ctus',
            'layout': { 'visibility': 'none' },
            'paint': { 'fill-color': '#33C8BC', "fill-opacity": 0.7, 'fill-outline-color': '#6d6d6d' }
          }, firstSymbolId);
  
          map.addLayer({
            "id": "ctus-fills-hover",
            "type": "fill",
            'source': 'ctus',
            'filter': ["==", "CTU_ID", ""],
            'paint': { 'fill-color': '#33C8BC', "fill-opacity": 0 }
          }, firstSymbolId);
  
          map.addSource('counties', {
            'type': 'geojson',
            'data': MetroCounties
          });
          map.addLayer({
            'id': 'county_boundaries',
            'type': 'line',
            'source': 'counties',
            'layout': { 'visibility': 'visible' },
            'paint': { 'line-color': '#e3803f', 'line-width': 3 }
          }, firstSymbolId);
  
          map.addLayer({
            'id': 'counties',
            'type': 'fill',
            'source': 'counties',
            'layout': { 'visibility': 'none' },
            'paint': { 'fill-color': '#e3803f', "fill-opacity": 0.5, 'fill-outline-color': '#6d6d6d' }
          }, firstSymbolId);
  
          map.addLayer({
            'id': 'Population',
            'type': 'fill',
            'source': 'ctus',
            'layout': { 'visibility': 'none' },
            'paint': {
              'fill-color': {
                property: 'pop20',
                stops: [[1000, '#9bbbf2'], [5000, '#8395b3'], [15000, '#606c82'], [30000, '#364766'], [50000, '#18273f'], [429954, '#061123']]
              },
              'fill-opacity': 0.8
            }
          }, firstSymbolId);
  
          map.addLayer({
            'id': 'Policy Count by City',
            'type': 'fill',
            'source': 'ctus',
            'layout': { 'visibility': 'none' },
            'paint': {
              'fill-color': {
                property: 'policy_ct',
                stops: [[0, '#fafdf4'], [5, '#e5f2d2'], [12, '#9cc066'], [25, '#657945'], [31, '#2d381d']]
              },
              'fill-opacity': 0.8
            }
          }, firstSymbolId);
  
          map.addLayer({
            'id': 'Count by County-Adopted Policies',
            'type': 'fill',
            'source': 'counties',
            'layout': { 'visibility': 'none' },
            'paint': {
              'fill-color': {
                property: 'policy_ct',
                stops: [[0, '#feefa4'], [5, '#ecc781'], [7, '#dd8346'], [9, '#b44e1e']]
              },
              'fill-opacity': 0.6
            }
          }, firstSymbolId);
  
          map.addLayer({
            'id': 'Cultural Districts',
            'type': 'fill',
            'source': 'cult_dists',
            'layout': { 'visibility': 'none' },
            'paint': { 'fill-color': '#1f78b4', "fill-opacity": 0.7, 'fill-outline-color': '#1f78b4' }
          }, firstSymbolId);
  
          
          map.addLayer({
            'id': 'cd_labels',
            'type': 'symbol',
            'source': 'cd_labels',
            'layout': { 'visibility': 'none', 'text-field': ['get', 'name'], 'text-font': ['Open Sans Semibold'] },
            'paint': { 'text-color': '#1c4a69' }
          });
  
          // Popup and interaction logic
          map.on('mouseenter', 'ctus-main', (e) => {
            map.getCanvas().style.cursor = 'pointer';
          });
  
          map.on('mouseleave', 'ctus-main', () => {
            map.getCanvas().style.cursor = '';
          });
          
          // Helper function to create popups with smart positioning
          const createSmartPopup = (coordinates, htmlContent, estimatedHeight = 200) => {
            // Get current banner height
            const bannerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--banner-height') || '120');
            
            // Convert coordinates to screen pixels
            const screenPoint = map.project(coordinates);
            
            // Get map container dimensions
            const mapContainer = map.getContainer();
            const screenWidth = mapContainer.offsetWidth;
            const screenHeight = mapContainer.offsetHeight;
            
            // Calculate potential collision areas
            const popupWidth = 300; // Max width from CSS
            const buffer = 20;
            
            // Check various collision scenarios
            const wouldHitTop = screenPoint.y - estimatedHeight < bannerHeight + buffer;
            const wouldHitBottom = screenPoint.y + estimatedHeight > screenHeight - buffer;
            const wouldHitLeft = screenPoint.x - popupWidth/2 < buffer;
            const wouldHitRight = screenPoint.x + popupWidth/2 > screenWidth - buffer;
            
            // Determine optimal anchor and offset
            let anchor = 'bottom';
            let offset = [0, -10];
            
            // Vertical positioning logic
            if (wouldHitTop && !wouldHitBottom) {
              // Show popup below the point
              anchor = 'top';
              offset = [0, 10];
            } else if (wouldHitBottom && !wouldHitTop) {
              // Show popup above the point
              anchor = 'bottom';
              offset = [0, -10];
            } else if (wouldHitTop && wouldHitBottom) {
              // If both top and bottom would be cut off, center it and allow scrolling
              anchor = 'center';
              offset = [0, 0];
            }
            
            // Horizontal positioning adjustments
            if (wouldHitLeft && !wouldHitRight) {
              // Shift popup to the right
              if (anchor === 'bottom') anchor = 'bottom-left';
              else if (anchor === 'top') anchor = 'top-left';
              else if (anchor === 'center') anchor = 'left';
            } else if (wouldHitRight && !wouldHitLeft) {
              // Shift popup to the left
              if (anchor === 'bottom') anchor = 'bottom-right';
              else if (anchor === 'top') anchor = 'top-right';
              else if (anchor === 'center') anchor = 'right';
            }
            
            return new mapboxgl.Popup({ 
              closeButton: true,
              closeOnClick: false,
              anchor: anchor,
              offset: offset,
              maxWidth: '250px' // Ensure consistent width
            })
            .setLngLat(coordinates)
            .setHTML(htmlContent);
          };

          map.on("click", "ctus-main", (e) => {
            // Clear any existing map-click popup
            if (mapClickPopupRef.current) {
              mapClickPopupRef.current.remove();
              mapClickPopupRef.current = null;
            }

            // Clear any ZoomTo popup
            if (zoomToRef.current) {
              zoomToRef.current.clearZoomToPopup();
            }

            // If the click also intersects with a county feature (either centroid label or boundary)
            // we want to ignore this handler so that only the county popup appears.
            const countyClickLayers = ['centroids'];
            const countyFeaturesAtPoint = map.queryRenderedFeatures(e.point, { layers: countyClickLayers });
            if (countyFeaturesAtPoint && countyFeaturesAtPoint.length > 0) {
              return; // Let the county-specific click handler handle this interaction
            }
            if (e.features && e.features.length > 0) {
              // Clear any ZoomTo selection first
              setIsZoomToOpen(false);
              map.setLayoutProperty('ctus-darker', 'visibility', 'none');
              map.setLayoutProperty('counties', 'visibility', 'none');
              map.setFilter('ctus-darker', null);
              map.setFilter('counties', null);

              map.setFilter("ctus-fills-hover", ["==", "CTU_ID", e.features[0].properties.CTU_ID]);
              var ctu_name = e.features[0].properties.CTU_NAME;
              var policy_ct = e.features[0].properties.policy_ct;
              var cat_count = e.features[0].properties.cat_count;
              var categories = '';
              for (let i = 1; i <= cat_count; i++) {
                categories += `<br>${e.features[0].properties['_cat' + i]}`;
              }
              var coordinates = e.lngLat;

              // Estimate popup height based on content
              const basePopupHeight = 160; // Base height for title, footer, buttons etc.
              const heightPerCategory = 18; // Approximate height of a line of text for a category
              const estimatedPopupHeight = basePopupHeight + (cat_count * heightPerCategory);

              // Find matching link from MCLinks
              const normalizedCtuName = normalizeEntityName(ctu_name);
              const linkData = MCLinks.city_township_profiles.find(profile => profile.name === normalizedCtuName) || 
                             MCLinks.county_profiles.find(profile => profile.name === normalizedCtuName);
              const hasLink = linkData && linkData.url;
              
              const cityNameDisplay = `<h3 style="color: #091732">${ctu_name}</h3>`;

              const popupContent = `
                <div class="popup-content">
                  ${cityNameDisplay}
                  <b>Number of reported policies: </b>${policy_ct}
                  ${categories}
                  <div class="popup-footer">
                    <button 
                      onclick="document.dispatchEvent(new CustomEvent('openTableView', { detail: { name: '${ctu_name}', type: 'city' }}))" 
                      class="table-view-btn"
                      style="background-color:#091732; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; font-size: 12px; width: 180px; font-weight: normal;"
                    >Open Policy Table</button>
                    ${hasLink ? 
                      `<a href="${linkData.url}" target="_blank" style="text-decoration: none;">
                        <button style="background-color:#091732; color: white; border: none; padding: 8px 16px; border-radius: 4px; margin-top: 4px; cursor: pointer; font-size: 12px; width: 180px;">
                          Open Community Profile
                        </button>
                      </a>` :
                      `<button style="background-color:#091732; color: white; border: none; padding: 8px 16px; border-radius: 4px; margin-top: 4px; cursor: pointer; font-size: 12px; opacity: 0.5; width: 180px;" disabled>
                        Open Community Profile
                      </button>`
                    }
                  </div>
                </div>
              `;

              const popup = createSmartPopup(coordinates, popupContent, estimatedPopupHeight)
                .addTo(map);

              // Store the popup reference
              mapClickPopupRef.current = popup;

              // Add event listener for the custom event
              const handleTableViewClick = (event) => {
                openModal('tableView', event.detail);
              };
              document.addEventListener('openTableView', handleTableViewClick);

              // Remove event listener and reference when popup is closed
              popup.on('close', () => {
                document.removeEventListener('openTableView', handleTableViewClick);
                mapClickPopupRef.current = null;
              });
            }
          });
  
          // Add hover effects for centroids (county names)
          map.on('mouseenter', 'centroids', (e) => {
            map.getCanvas().style.cursor = 'pointer';
          });

          map.on('mouseleave', 'centroids', () => {
            map.getCanvas().style.cursor = '';
          });

          // Add click handler for centroids (county names)
          map.on("click", "centroids", (e) => {
            // Clear any existing map-click popup
            if (mapClickPopupRef.current) {
              mapClickPopupRef.current.remove();
              mapClickPopupRef.current = null;
            }

            // Clear any ZoomTo popup
            if (zoomToRef.current) {
              zoomToRef.current.clearZoomToPopup();
            }

            // Prevent the event from bubbling up to other click handlers (e.g., the city layer)
            if (e.originalEvent && e.originalEvent.stopPropagation) {
              e.originalEvent.stopPropagation();
            }
            if (e.features && e.features.length > 0) {
              // Clear any ZoomTo selection first
              setIsZoomToOpen(false);
              map.setLayoutProperty('ctus-darker', 'visibility', 'none');
              map.setLayoutProperty('counties', 'visibility', 'none');
              map.setFilter('ctus-darker', null);
              map.setFilter('counties', null);

              const centroidFeature = e.features[0];
              const county_name = centroidFeature.properties.CO_NAME;
              
              // Find the corresponding county feature from the counties source to get policy data
              const countyFeatures = map.querySourceFeatures('counties', {
                filter: ['==', 'CO_NAME', county_name]
              });
              
              let policy_ct = 0;
              let cat_count = 0;
              let categories = '';
              
              if (countyFeatures && countyFeatures.length > 0) {
                const countyFeature = countyFeatures[0];
                policy_ct = countyFeature.properties.policy_ct || 0;
                cat_count = countyFeature.properties.cat_count || 0;
                
                // Build categories string if categories exist
                for (let i = 1; i <= cat_count; i++) {
                  if (countyFeature.properties['_cat' + i]) {
                    categories += `<br>${countyFeature.properties['_cat' + i]}`;
                  }
                }
              }

              // Set county highlight
              map.setLayoutProperty('counties', 'visibility', 'visible');
              map.setFilter('counties', ['==', 'CO_NAME', county_name]);

              // Format county name for display and link lookup
              const formattedCountyName = county_name.charAt(0).toUpperCase() + county_name.slice(1).toLowerCase();
              const countyLinkData = MCLinks.county_profiles.find(profile => profile.name === `${formattedCountyName} County`);
              const hasCountyLink = countyLinkData && countyLinkData.url;

              // Estimate popup height
              const countyBaseHeight = 160;
              const countyHeightPerCategory = 18;
              const countyPopupHeight = countyBaseHeight + (cat_count * countyHeightPerCategory);

              // Create and show the popup with smart positioning
              const countyPopupContent = `
                <div class="popup-content">
                  <h3 style="color: #091732">${formattedCountyName} County</h3>
                  <b>Number of reported policies: </b>${policy_ct}
                  ${categories}
                  <div class="popup-footer">
                    <button 
                      onclick="document.dispatchEvent(new CustomEvent('openTableView', { detail: { name: '${county_name}', type: 'county' }}))" 
                      class="table-view-btn"
                      style="background-color:#091732; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; font-size: 12px; width: 180px; font-weight: normal;"
                    >Open Policy Table</button>
                    ${hasCountyLink ? 
                      `<a href="${countyLinkData.url}" target="_blank" style="text-decoration: none;">
                        <button style="background-color:#091732; color: white; border: none; padding: 8px 16px; border-radius: 4px; margin-top: 4px; cursor: pointer; font-size: 12px; width: 180px;">
                          Open Community Profile
                        </button>
                      </a>` :
                      `<button style="background-color:#091732; color: white; border: none; padding: 8px 16px; border-radius: 4px; margin-top: 4px; cursor: pointer; font-size: 12px; opacity: 0.5; width: 180px;" disabled>
                        Open Community Profile
                      </button>`
                    }
                  </div>
                </div>
              `;

              const popup = createSmartPopup(e.lngLat, countyPopupContent, countyPopupHeight)
                .addTo(map);

              // Store the popup reference
              mapClickPopupRef.current = popup; 

              // Add event listener for the custom event
              const handleTableViewClick = (event) => {
                openModal('tableView', event.detail);
              };
              document.addEventListener('openTableView', handleTableViewClick);

              // Remove event listener when popup is closed
              popup.on('close', () => {
                document.removeEventListener('openTableView', handleTableViewClick);
                mapClickPopupRef.current = null;
                // Clear county highlight when popup is closed
                map.setLayoutProperty('counties', 'visibility', 'none');
                map.setFilter('counties', null);
              });
            }
          });

          map.on("click", "county_boundaries", (e) => {
            // Clear any existing map-click popup
            if (mapClickPopupRef.current) {
              mapClickPopupRef.current.remove();
              mapClickPopupRef.current = null;
            }

            // Clear any ZoomTo popup
            if (zoomToRef.current) {
              zoomToRef.current.clearZoomToPopup();
            }

            // Prevent the event from bubbling up to other click handlers (e.g., the city layer)
            if (e.originalEvent && e.originalEvent.stopPropagation) {
              e.originalEvent.stopPropagation();
            }
            if (e.features && e.features.length > 0) {
              // Clear any ZoomTo selection first
              setIsZoomToOpen(false);
              map.setLayoutProperty('ctus-darker', 'visibility', 'none');
              map.setLayoutProperty('counties', 'visibility', 'none');
              map.setFilter('ctus-darker', null);
              map.setFilter('counties', null);

              const feature = e.features[0];
              const county_name = feature.properties.CO_NAME;
              const policy_ct = feature.properties.policy_ct || 0;
              const cat_count = feature.properties.cat_count || 0;
              let categories = '';
              
              // Build categories string if categories exist
              for (let i = 1; i <= cat_count; i++) {
                if (feature.properties['_cat' + i]) {
                  categories += `<br>${feature.properties['_cat' + i]}`;
                }
              }

              // Set county highlight
              map.setLayoutProperty('counties', 'visibility', 'visible');
              map.setFilter('counties', ['==', 'CO_NAME', county_name]);

              // Format county name for display and link lookup
              const formatCountyName = county_name.charAt(0).toUpperCase() + county_name.slice(1).toLowerCase();
              const countyLinkData = MCLinks.county_profiles.find(profile => profile.name === `${formatCountyName} County`);
              const hasCountyLink = countyLinkData && countyLinkData.url;

              // Estimate popup height
              const boundaryBaseHeight = 160;
              const boundaryHeightPerCategory = 18;
              const boundaryPopupHeight = boundaryBaseHeight + (cat_count * boundaryHeightPerCategory);

              // Create and show the popup with smart positioning
              const boundaryPopupContent = `
                <div class="popup-content">
                  <h3 style="color: #091732">${formatCountyName} County</h3>
                  <b>Number of county-adopted policies: </b>${policy_ct}
                  ${categories}
                  <div class="popup-footer">
                    <button 
                      onclick="document.dispatchEvent(new CustomEvent('openTableView', { detail: { name: '${county_name}', type: 'county' }}))" 
                      class="table-view-btn"
                      style="background-color:#091732; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; font-size: 12px; width: 180px; font-weight: normal;"
                    >Open Policy Table</button>
                    ${hasCountyLink ? 
                      `<a href="${countyLinkData.url}" target="_blank" style="text-decoration: none;">
                        <button style="background-color:#091732; color: white; border: none; padding: 8px 16px; border-radius: 4px; margin-top: 4px; cursor: pointer; font-size: 12px; width: 180px;">
                          Open Community Profile
                        </button>
                      </a>` :
                      `<button style="background-color:#091732; color: white; border: none; padding: 8px 16px; border-radius: 4px; margin-top: 4px; cursor: pointer; font-size: 12px; opacity: 0.5; width: 180px;" disabled>
                        Open Community Profile
                      </button>`
                    }
                  </div>
                </div>
              `;

              const popup = createSmartPopup(e.lngLat, boundaryPopupContent, boundaryPopupHeight)
                .addTo(map);

              // Store the popup reference
              mapClickPopupRef.current = popup;

              // Add event listener for the custom event
              const handleTableViewClick = (event) => {
                openModal('tableView', event.detail);
              };
              document.addEventListener('openTableView', handleTableViewClick);

              // Remove event listener when popup is closed
              popup.on('close', () => {
                document.removeEventListener('openTableView', handleTableViewClick);
                mapClickPopupRef.current = null;
                // Clear county highlight when popup is closed
                map.setLayoutProperty('counties', 'visibility', 'none');
                map.setFilter('counties', null);
              });
            }
          });

          // Set map instance at the end of the load event
          setMapInstance(map);
          console.log("Map instance set");
        });

        return () => {
          if (map) {
            console.log("Removing map");
            map.remove();
          }
        };
      }
    }, [mapContainer]);

    useEffect(() => {
      if (mapInstance && mapInstance.isStyleLoaded && mapInstance.isStyleLoaded()) {
        try {
          if (mapInstance.getLayer("Population")) {
            const populationLayer = layersFilters.find(layer => layer.layer === "Population");
            mapInstance.setLayoutProperty(
              "Population", 
              "visibility", 
              populationLayer && populationLayer.active ? "visible" : "none"
            );
          }
        } catch (error) {
          console.warn("Error updating Population layer:", error);
        }
      }
    }, [mapInstance, layersFilters]);

    // Add a new useEffect to check the map container dimensions
    useEffect(() => {
      if (mapContainer.current) {
        const containerWidth = mapContainer.current.offsetWidth;
        const containerHeight = mapContainer.current.offsetHeight;
        console.log(`Map container dimensions: ${containerWidth}x${containerHeight}`);
        
        if (containerWidth === 0 || containerHeight === 0) {
          console.warn("Map container has zero width or height. Map might not render properly.");
        }
      }
    }, []);

    // Get all active legends that should be displayed
    const getActiveLegends = () => {
      const legends = [];

      // Check if we have any filters other than geography type
      const hasNonGeographyFilters = filters.policyTypes.length > 0 || 
                                    filters.policySubtypes.length > 0 ||
                                    (filters.populationRange && filters.populationRange.minThreshold !== undefined) ||
                                    (filters.cityPopulation && filters.cityPopulation.minThreshold !== undefined) ||
                                    (filters.countyPopulation && filters.countyPopulation.minThreshold !== undefined);

      // Check if policy filters are active
      const hasPolicyFilters = filters.policyTypes.length > 0 || filters.policySubtypes.length > 0;

      // City Population Legend - only show if no policy filters are active
      if (!hasPolicyFilters && filters.cityPopulation && filters.cityPopulation.activeRanges && filters.cityPopulation.activeRanges.length > 0) {
        legends.push({
          title: "City Population Ranges (Selected)",
          items: filters.cityPopulation.activeRanges.map(range => ({
            color: range.color,
            label: range.label
          })),
          type: 'cityPopulation'
        });
      }

      // County Population Legend - only show if no policy filters are active
      if (!hasPolicyFilters && filters.countyPopulation && filters.countyPopulation.activeRanges && filters.countyPopulation.activeRanges.length > 0) {
        legends.push({
          title: "County Population Ranges (Selected)",
          items: filters.countyPopulation.activeRanges.map(range => ({
            color: range.color,
            label: range.label
          })),
          type: 'countyPopulation'
        });
      }

      // Legacy population range support - only show if no policy filters are active
      if (!hasPolicyFilters && filters.populationRange && filters.populationRange.activeRanges && filters.populationRange.activeRanges.length > 0) {
        legends.push({
          title: "Population Ranges (Selected)",
          items: filters.populationRange.activeRanges.map(range => ({
            color: range.color,
            label: range.label
          })),
          type: 'populationRange'
        });
      }

      // Layer-based legends
      if (activeLayer === "Population") {
        legends.push({
          title: "Population",
          items: populationLegend,
          type: 'layerPopulation'
        });
      } else if (activeLayer === "Policy Count by City") {
        legends.push({
          title: "Policy Count by City",
          items: policyLegend,
          type: 'layerPolicy'
        });
      } else if (activeLayer === "Count by County-Adopted Policies") {
        legends.push({
          title: "Count by County-Adopted Policies",
          items: countyLegend,
          type: 'layerCounty'
        });
      }

      // Policy Types Legend - only show selected policy types or subtypes
      if (hasNonGeographyFilters && (filters.policyTypes.length > 0 || filters.policySubtypes.length > 0)) {
        let selectedPolicyTypes = [];
        
        // If policy types are directly selected, use those
        if (filters.policyTypes.length > 0) {
          selectedPolicyTypes = filters.policyTypes;
        } 
        // If only subtypes are selected, find their parent policy types
        else if (filters.policySubtypes.length > 0) {
          const parentTypes = new Set();
          Object.entries(policySubtypeOptions).forEach(([parentType, subtypes]) => {
            if (subtypes.some(subtype => filters.policySubtypes.includes(subtype.value))) {
              parentTypes.add(parentType);
            }
          });
          selectedPolicyTypes = Array.from(parentTypes);
        }

        // Create legend items only for selected policy types
        const selectedPolicyTypesLegend = selectedPolicyTypes.map(policyType => {
          const option = policyTypeOptions.find(opt => opt.value === policyType);
          return {
            color: option?.color || '#000000',
            label: option?.label || policyType
          };
        });

        if (selectedPolicyTypesLegend.length > 0) {
          legends.push({
            title: "Selected Policies",
            items: selectedPolicyTypesLegend,
            type: 'policyTypes'
          });
        }
      }

      return legends;
    };

    // Add event listener for opening table view
    useEffect(() => {
        const handleOpenTable = (event) => {
            openModal('tableView', event.detail);
            setIsFilterMenuVisible(false); // Close the filter menu when opening table
        };

        window.addEventListener('openTableView', handleOpenTable);
        return () => window.removeEventListener('openTableView', handleOpenTable);
    }, [openModal]);

    return (
      <>
        <MenuButtons 
          map={mapInstance} 
          onLayerChange={setActiveLayer} 
          activeLayer={activeLayer} 
          onFilterChange={handleFilterChange}
          toggleFilterMenu={toggleFilterMenu}
          toggleZoomTo={toggleZoomTo}
          isFilterActive={filters.policyTypes.length > 0 || 
                           filters.policySubtypes.length > 0 || 
                           filters.communitySize.length > 0 || 
                           filters.geographyType.length > 0 ||
                           (filters.populationRange && filters.populationRange.minThreshold !== undefined) ||
                           (filters.cityPopulation && filters.cityPopulation.minThreshold !== undefined) ||
                           (filters.countyPopulation && filters.countyPopulation.minThreshold !== undefined)}
          filters={filters}                 
          closeFilterMenu={closeFilterMenu}
          closeZoomToMenu={closeZoomToMenu}
        />
        {isFilterMenuVisible && (
          <FilterMenu onFilterChange={handleFilterChange} filters={filters} clearFiltersTrigger={clearFiltersTrigger} />
        )}
        
        {/* Add the ZoomTo component with proper props */}
        {mapInstance && (
          <ZoomTo 
            ref={zoomToRef}
            map={mapInstance} 
            counties={MetroCounties}
            ctus={MetroCTU}
            isOpen={isZoomToOpen}
            toggleOpen={toggleZoomTo}
            mapClickPopupRef={mapClickPopupRef}
          />
        )}
        
        <div 
          ref={mapContainer} 
          style={{ 
            width: "100%", 
            height: "calc(100vh - var(--banner-height, 120px))", 
            position: "absolute", 
            top: "var(--banner-height, 120px)", 
            left: 0,
            zIndex: 0 
          }} 
        />
        {/* Show legend when activeLayer is set OR when any filters are active */}
        {getActiveLegends().map((legend, index) => (
          <Legend
            key={`${legend.type}-${index}`}
            title={legend.title}
            items={legend.items}
            activeLayer={activeLayer}
            isMultiple={getActiveLegends().length > 1}
            legendIndex={index}
          />
        ))}
      </>
    );
  
  };

Map.propTypes = {
    filters: PropTypes.shape({
        policyTypes: PropTypes.array,
        policySubtypes: PropTypes.array,
        communitySize: PropTypes.array,
        geographyType: PropTypes.array,
        populationRange: PropTypes.object
    })
};

export default Map;
